import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { Loader2, Zap } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { useLanguage } from "../i18n";
import { usePageMeta } from "../hooks/usePageMeta";
import { useAuth } from "../auth";
import KycModal from "../components/KycModal";
import PaymentMethodDialog from "../components/PaymentMethodDialog";
import PhoneGateModal from "../components/PhoneGateModal";
import { Reveal } from "../components/Reveal";

function TopupRows({ tops, onBuy, busy, t, fmt }) {
  if (tops === null) {
    return <div className="py-6 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[#FF6B35]" /></div>;
  }
  if (tops.length === 0) {
    return <p className="text-sm text-slate-400">{t("cat.none")}</p>;
  }
  return (
    <div className="space-y-2.5">
      {tops.map((p) => (
        <div key={p.package_id} data-testid={`topup-row-${p.package_id}`}
          className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200/80 px-5 py-3.5 hover:border-[#FF6B35]/50 transition-colors">
          <div>
            <span className="font-display font-bold text-slate-900">{p.is_unlimited ? t("dest.unlimited") : p.data}</span>
            {p.validity && <span className="ml-2 font-mono-t text-[10px] uppercase tracking-[0.15em] text-slate-400">{p.validity} {t("dest.days")}</span>}
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="font-mono-t text-sm font-bold text-[#FF6B35]">{fmt(p.price)}</span>
            <button data-testid={`topup-buy-${p.package_id}`} onClick={() => onBuy(p)} disabled={busy === p.package_id}
              className="rounded-full bg-[#FF6B35] text-white px-5 py-2 font-mono-t text-[10px] font-bold uppercase tracking-[0.15em] hover:bg-[#E8551F] transition-colors disabled:opacity-60 flex items-center justify-center gap-2 text-center leading-snug">
              {busy === p.package_id && <Loader2 className="w-3 h-3 animate-spin shrink-0" />}
              {t("dest.buy")}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function SimTopup({ sim }) {
  const { t, fmt, currency, lang } = useLanguage();
  const { user, checkAuth } = useAuth();
  const [tops, setTops] = useState(null);
  const [busy, setBusy] = useState(null);
  const [kycOpen, setKycOpen] = useState(false);
  const pending = useRef(null);                 // { p, method: "wallet" | "card" | "new_card" }
  const [payPkg, setPayPkg] = useState(null);
  const [payBusy, setPayBusy] = useState(null);
  const [savedCard, setSavedCard] = useState(null);
  const [phoneGate, setPhoneGate] = useState(null); // null | "certify" | "confirm"

  // La lista ricariche può essersi aggiornata durante il giro SMS: ricarica e riprova UNA volta.
  const pkgRetried = useRef(new Set());
  const loadTops = () => api.get(`/esims/${sim.iccid}/topups`).then(({ data }) => {
    setTops(data.data || []);
    return data.data || [];
  }).catch(() => { setTops([]); return []; });
  const handlePkgNotFound = async (p, retryFn) => {
    const fresh = await loadTops();
    const p2 = fresh.find((x) => x.package_id === p.package_id);
    if (p2 && !pkgRetried.current.has(p.package_id)) {
      pkgRetried.current.add(p.package_id);
      retryFn(p2);
    } else {
      pending.current = null;
      toast.error(t("pay.pkgGone"));
    }
  };

  // 428 dal pagamento: instrada al modulo giusto (telefono/SMS ordine o dati fiscali)
  const handleGate = (e, method, p) => {
    pending.current = { p, method };
    setPayPkg(null);
    setPayBusy(null);
    const det = e.response?.data?.detail;
    // token dispositivo affidabile scaduto o revocato: lo tolgo così ricompare la spunta "ricorda"
    if (det === "order_otp_required") localStorage.removeItem("simamma_td");
    if (det === "phone_required" || det === "order_otp_required") {
      setPhoneGate(det === "phone_required" ? "certify" : "confirm");
    } else {
      setKycOpen(true);
    }
  };

  useEffect(() => {
    if (!user) { setSavedCard(null); return; }
    api.get("/payments/saved-card").then(({ data }) => setSavedCard(data.data || { saved: false })).catch(() => setSavedCard({ saved: false }));
  }, [user]);

  const navigateRefresh = () => window.location.assign("/account?tab=esims");

  const walletBuy = async (p) => {
    setPayBusy("wallet");
    try {
      await api.post("/wallet/order", { package_id: p.package_id, iccid: sim.iccid });
      toast.success(t("wallet.orderDone"));
      setPayPkg(null);
      setPayBusy(null);
      await checkAuth();
      navigateRefresh();
    } catch (e) {
      const st = e.response?.status;
      if (st === 428) {
        handleGate(e, "wallet", p);
      } else if (st === 402) {
        toast.error(t("wallet.insufficient"));
      } else if (e.response?.data?.detail === "Package not found") {
        setPayBusy(null);
        handlePkgNotFound(p, walletBuy);
        return;
      } else {
        toast.error(e.response?.data?.detail || t("misc.error"));
      }
      setPayBusy(null);
    }
  };

  const cardBuy = async (p) => {
    setPayBusy("card");
    try {
      const { data: res } = await api.post("/payments/pay-with-saved-card", { package_id: p.package_id, iccid: sim.iccid });
      if (res.status === "requires_action") {
        // 3DS non gestito in-page sul web: si prosegue col checkout Stripe completo
        toast.info(t("pay.cardAction"));
        setPayBusy(null);
        checkoutStripe(p);
        return;
      }
      toast.success(t("pay.orderDoneCard"));
      setPayPkg(null);
      setPayBusy(null);
      await checkAuth();
      navigateRefresh();
    } catch (e) {
      const st = e.response?.status;
      const det = e.response?.data?.detail;
      if (st === 428) {
        handleGate(e, "card", p);
      } else if (st === 402) {
        toast.error(t("pay.cardDeclined"));
      } else if (det === "no_saved_card") {
        setSavedCard({ saved: false });
      } else if (det === "Package not found") {
        setPayBusy(null);
        handlePkgNotFound(p, cardBuy);
        return;
      } else {
        toast.error(det === "provisioning_failed_refunded" ? t("pay.failedProv") : det === "airalo_credit_pending" ? t("pay.creditPending") : (det || t("misc.error")));
      }
      setPayBusy(null);
    }
  };

  const checkoutStripe = async (p) => {
    setBusy(p.package_id);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: p.package_id,
        iccid: sim.iccid,
        origin_url: window.location.origin,
        currency,
        lang,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      if (e.response?.status === 428) {
        handleGate(e, "new_card", p);
      } else if (e.response?.data?.detail === "Package not found") {
        setBusy(null);
        setPayBusy(null);
        handlePkgNotFound(p, checkoutStripe);
        return;
      } else {
        toast.error(e.response?.data?.detail || t("misc.error"));
      }
      setBusy(null);
      setPayBusy(null);
    }
  };

  const startBuy = (p) => {
    if ((user?.wallet_balance || 0) > 0 || savedCard?.saved) {
      setPayPkg(p);
      return;
    }
    checkoutStripe(p);
  };

  const onPickMethod = (method) => {
    const p = payPkg;
    if (!p) return;
    if (method === "wallet") walletBuy(p);
    else if (method === "card") cardBuy(p);
    else checkoutStripe(p);
  };

  const retryPending = () => {
    const pend = pending.current;
    if (!pend) return;
    if (pend.method === "wallet") walletBuy(pend.p);
    else if (pend.method === "card") cardBuy(pend.p);
    else checkoutStripe(pend.p);
  };

  useEffect(() => {
    loadTops();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sim.iccid]);

  return (
    <div data-testid={`topup-sim-${sim.iccid}`} className="rounded-3xl bg-white border border-slate-200/80 shadow-sm p-7">
      <div className="flex items-center justify-between gap-4 mb-5">
        <div>
          <div className="font-display font-bold text-xl text-slate-900">{sim.package_title}</div>
          <div className="font-mono-t text-[10px] uppercase tracking-[0.2em] text-slate-400 mt-1">
            {sim.destination} · {sim.iccid.slice(-8)} {sim.mock && `· ${t("acc.demo")}`}
          </div>
        </div>
        <span className="w-10 h-10 rounded-full bg-[#FF6B35]/10 text-[#FF6B35] flex items-center justify-center shrink-0">
          <Zap className="w-5 h-5" />
        </span>
      </div>
      <TopupRows tops={tops} onBuy={startBuy} busy={busy} t={t} fmt={fmt} />
      <PaymentMethodDialog
        open={!!payPkg}
        onOpenChange={(v) => { if (!v) { setPayPkg(null); setPayBusy(null); } }}
        title={payPkg ? `${payPkg.is_unlimited ? t("dest.unlimited") : payPkg.data}${payPkg.validity ? ` · ${payPkg.validity} ${t("dest.days")}` : ""}` : ""}
        priceLabel={payPkg ? fmt(payPkg.price) : ""}
        walletBalance={user?.wallet_balance || 0}
        card={savedCard}
        busy={payBusy || (payPkg && busy === payPkg.package_id ? "new_card" : null)}
        onPick={onPickMethod}
      />
      <PhoneGateModal open={!!phoneGate} mode={phoneGate || "confirm"}
        onOpenChange={(v) => { if (!v) setPhoneGate(null); }}
        onDone={() => { setPhoneGate(null); retryPending(); }} />
      <KycModal open={kycOpen} onOpenChange={setKycOpen} onDone={retryPending} />
    </div>
  );
}

function SharedTopup({ token }) {
  const { t, fmt, currency, lang } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(null);
  const [kycOpen, setKycOpen] = useState(false);
  const pending = useRef(null);
  const [phoneGate, setPhoneGate] = useState(null); // null | "certify" | "confirm"

  useEffect(() => {
    api.get(`/topup-link/${token}`).then(({ data }) => setData(data.data)).catch(() => setError(true));
  }, [token]);

  useEffect(() => {
    if (!user || !data) return;
    try {
      const pend = JSON.parse(sessionStorage.getItem("atlas_pending_topup") || "null");
      if (pend && pend.path === location.pathname) {
        sessionStorage.removeItem("atlas_pending_topup");
        const pkg = (data.topups || []).find((x) => x.package_id === pend.package_id);
        if (pkg) buy(pkg);
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, data]);

  const buy = async (p) => {
    if (!user) {
      try {
        sessionStorage.setItem("atlas_pending_topup", JSON.stringify({ package_id: p.package_id, path: location.pathname }));
      } catch { /* ignore */ }
      navigate(`/account?next=${encodeURIComponent(location.pathname)}`);
      return;
    }
    setBusy(p.package_id);
    try {
      const { data: res } = await api.post("/payments/checkout", {
        package_id: p.package_id,
        iccid: data.iccid,
        share_token: token,
        origin_url: window.location.origin,
        currency,
        lang,
      });
      window.location.href = res.checkout_url;
    } catch (e) {
      if (e.response?.status === 428) {
        pending.current = p;
        const det = e.response?.data?.detail;
        // stesso gate del checkout normale: prima il telefono, poi i dati fiscali
        if (det === "order_otp_required") localStorage.removeItem("simamma_td");
        if (det === "phone_required" || det === "order_otp_required") {
          setPhoneGate(det === "phone_required" ? "certify" : "confirm");
        } else {
          setKycOpen(true);
        }
      } else {
        toast.error(e.response?.data?.detail || t("misc.error"));
      }
      setBusy(null);
    }
  };

  if (error) {
    return <div data-testid="topup-link-invalid" className="rounded-3xl border border-dashed border-slate-300 py-16 text-center text-slate-500">{t("misc.error")}</div>;
  }
  if (!data) {
    return <div className="py-16 text-center"><Loader2 className="w-7 h-7 animate-spin mx-auto text-[#FF6B35]" /></div>;
  }
  return (
    <div data-testid={`topup-shared-${data.iccid}`} className="rounded-3xl bg-white border border-slate-200/80 shadow-sm p-7">
      <div className="flex items-center justify-between gap-4 mb-5">
        <div>
          <div className="font-display font-bold text-xl text-slate-900">{data.package_title}</div>
          <div className="font-mono-t text-[10px] uppercase tracking-[0.2em] text-slate-400 mt-1">
            {data.destination} · …{data.iccid.slice(-8)} {data.mock && `· ${t("acc.demo")}`}
          </div>
        </div>
        <span className="w-10 h-10 rounded-full bg-[#FF6B35]/10 text-[#FF6B35] flex items-center justify-center shrink-0">
          <Zap className="w-5 h-5" />
        </span>
      </div>
      <TopupRows tops={data.topups} onBuy={buy} busy={busy} t={t} fmt={fmt} />
      <PhoneGateModal open={!!phoneGate} mode={phoneGate || "confirm"}
        onOpenChange={(v) => { if (!v) setPhoneGate(null); }}
        onDone={() => { setPhoneGate(null); const p = pending.current; if (p) buy(p); }} />
      <KycModal open={kycOpen} onOpenChange={setKycOpen} onDone={() => { const p = pending.current; if (p) buy(p); }} />
    </div>
  );
}

export default function TopupPage() {
  usePageMeta("seo.topupTitle", "seo.topupDesc");
  const { t } = useLanguage();
  const { user } = useAuth();
  const { token } = useParams();
  const navigate = useNavigate();
  const [esims, setEsims] = useState(null);

  useEffect(() => {
    if (!token && user === null) navigate(`/account?next=${encodeURIComponent("/topup")}`, { replace: true });
  }, [user, navigate, token]);

  useEffect(() => {
    if (!token && user) {
      api.get("/esims").then(({ data }) => setEsims(data.data || [])).catch(() => setEsims([]));
    }
  }, [user, token]);

  if (!token && !user) {
    return <div className="py-32 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-[#FF6B35]" /></div>;
  }

  return (
    <div data-testid="topup-page" className="px-4 sm:px-6 lg:px-12 max-w-3xl mx-auto py-12 sm:py-16">
      <Reveal>
        <div className="font-mono-t text-[11px] font-bold uppercase tracking-[0.3em] text-[#FF6B35] mb-3">{t("acc.topup")}</div>
        <h1 className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl text-slate-900">{t("topupPage.title")}</h1>
        <p className="mt-4 text-slate-600 max-w-lg">{t("topupPage.sub")}</p>
      </Reveal>

      <div className="mt-10 space-y-6">
        {token ? (
          <SharedTopup token={token} />
        ) : esims === null ? (
          <div className="py-16 text-center"><Loader2 className="w-7 h-7 animate-spin mx-auto text-[#FF6B35]" /></div>
        ) : esims.length === 0 ? (
          <div data-testid="topup-empty" className="rounded-3xl border border-dashed border-slate-300 py-16 text-center">
            <h3 className="font-display font-bold text-2xl text-slate-900">{t("acc.none")}</h3>
            <p className="mt-2 text-sm text-slate-500">{t("acc.noneSub")}</p>
            <Link to="/destinations" data-testid="topup-browse-link"
              className="inline-block mt-6 rounded-full bg-[#FF6B35] text-white px-8 py-3 font-mono-t text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#E8551F] transition-colors">
              {t("acc.browse")}
            </Link>
          </div>
        ) : (
          esims.map((sim) => <SimTopup key={sim.iccid} sim={sim} />)
        )}
      </div>
    </div>
  );
}
